import React, { useState, useEffect } from "react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import HeroSection from "../components/catalog/HeroSection";
import ProductCard from "../components/catalog/ProductCard";
import ProductDetailModal from "../components/catalog/ProductDetailModal";
import CartDrawer from "../components/cart/CartDrawer";
import { api } from "../services/api";
import { Search, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    api.get("/catalog/categories")
      .then((res) => {
        if (Array.isArray(res.data)) {
          setCategories(res.data);
        }
      })
      .catch(() => {});
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = {
        category: selectedCategory,
        sort_by: sortBy,
      };
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      const res = await api.get("/catalog/products", { params });
      setProducts(res.data?.products || []);
    } catch (err) {
      setProducts([]);
      toast.error("Não foi possível carregar os produtos agora. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, sortBy]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadProducts();
  };

  const handleScrollToVitrine = () => {
    const el = document.getElementById("vitrine-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white flex flex-col justify-between">
      <div>
        <Header />
        <HeroSection onExploreClick={handleScrollToVitrine} />

        {/* Catalog Section */}
        <section id="vitrine-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          
          {/* Section Heading */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-white/10">
            <div className="text-left">
              <span className="text-xs tracking-widest text-[#FF8C69] uppercase font-bold flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Catálogo Oficial</span>
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-1 text-white">
                Coleções Disponíveis
              </h2>
            </div>

            {/* Search Bar - only when products exist */}
            {products.length > 0 && (
              <form onSubmit={handleSearchSubmit} className="relative w-full md:w-80">
                <Search className="w-4 h-4 absolute left-3.5 top-3.5 text-[#A1A1AA]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar produtos..."
                  data-testid="catalog-search-input"
                  className="w-full bg-[#121214] border border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-xs text-white placeholder-[#A1A1AA]/50 focus:outline-none focus:border-[#FF8C69]"
                />
              </form>
            )}
          </div>

          {/* Filters & Sorting Bar - only when categories or products exist */}
          {categories.length > 0 && (
            <div className="py-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setSelectedCategory("all")}
                  data-testid="filter-cat-all"
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                    selectedCategory === "all"
                      ? "bg-[#FF8C69] text-[#0A0A0B] shadow-md"
                      : "bg-[#121214] text-[#A1A1AA] hover:text-white border border-white/10"
                  }`}
                >
                  Todos
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.category_id}
                    onClick={() => setSelectedCategory(cat.category_id)}
                    data-testid={`filter-cat-${cat.category_id}`}
                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                      selectedCategory === cat.category_id
                        ? "bg-[#FF8C69] text-[#0A0A0B] shadow-md"
                        : "bg-[#121214] text-[#A1A1AA] hover:text-white border border-white/10"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 text-xs">
                <span className="text-[#A1A1AA]">Ordenar por:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  data-testid="catalog-sort-select"
                  className="bg-[#121214] border border-white/10 rounded-xl py-2 px-3 text-xs text-white focus:outline-none focus:border-[#FF8C69]"
                >
                  <option value="popular">Destaques</option>
                  <option value="price_asc">Menor Preço</option>
                  <option value="price_desc">Maior Preço</option>
                  <option value="newest">Mais Recentes</option>
                </select>
              </div>
            </div>
          )}

          {/* Product Grid / Real Empty State */}
          {loading ? (
            <div className="py-16 text-center space-y-4">
              <p className="text-sm text-[#A1A1AA]">Carregando produtos...</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="rounded-2xl bg-[#121214] border border-white/10 h-80 animate-pulse" />
                ))}
              </div>
            </div>
          ) : products.length === 0 ? (
            <div className="py-20 text-center space-y-4 max-w-md mx-auto p-8 rounded-3xl bg-[#121214] border border-white/10 my-8">
              <h3 className="font-heading text-2xl font-bold text-white">
                Produtos indisponíveis no momento.
              </h3>
              <p className="text-xs text-[#A1A1AA] leading-relaxed">
                A loja está sendo preparada. Tente novamente mais tarde.
              </p>
              <button
                onClick={loadProducts}
                data-testid="btn-retry-catalog"
                className="mt-2 inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#FF8C69] hover:bg-[#FFA285] text-[#0A0A0B] text-xs font-bold transition-all"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Tentar novamente</span>
              </button>
            </div>
          ) : (
            <div
              data-testid="product-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {products.map((product) => (
                <ProductCard
                  key={product.product_id}
                  product={product}
                  onSelect={(p) => setSelectedProduct(p)}
                />
              ))}
            </div>
          )}

        </section>
      </div>

      <Footer />
      <CartDrawer />

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
}
